import { Button, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaBed, FaEdit, FaTrash, FaList, FaSave, FaTimes } from "react-icons/fa";
import ConfirmModal from "../../UI/ConfirmModal";
import { useState, useEffect, useContext } from "react";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import classes from "./Configuration.module.css";
import AuthContext from "../../../store/auth-context";

const Configuration = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);


  const initialForm = {
    id: 0,
    bedNumber: "",
    note: "",
    floor: "",
    wardName: "",
    description: "",
    errorMsg: ""
  };

  const [formData, setFormData] = useState(initialForm);
  const [wards, setWards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // States for Inline Editing
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingFormData, setEditingFormData] = useState({});

  // States for Confirm Modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);



  const fetchWards = async () => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    try {
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/ipd/wardBed/getAll`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        const data = await response.json();
        setWards(data || []);
      }
    } catch (err) {
      console.error("Error fetching wards:", err);
    }
  };

  useEffect(() => {
    fetchWards();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Character only validation for floor
    if (name === "floor") {
      const charOnly = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: charOnly,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setFormData(initialForm);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.floor.trim()) {
      toast.error("Floor is required");
      return;
    }
    if (!formData.wardName.trim()) {
      toast.error("Ward Name is required");
      return;
    }
    if (!formData.bedNumber) {
      toast.error("Bed Number is required");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/wardBed/addWardBed`;
      const token = getAuthToken();

      const payload = {
        ...formData,
        bedNumber: parseInt(formData.bedNumber, 10) || 0,
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error("Failed to save Ward/Bed configuration");
      }

      toast.success(formData.id > 0 ? "Configuration Updated Successfully!" : "Configuration Saved Successfully!");
      handleClear();
      fetchWards();

    } catch (error) {
      console.error(error);
      toast.error("Error saving configuration: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (ward) => {
    setEditingRowId(ward.id);
    setEditingFormData({ ...ward });
  };

  const handleInlineInputChange = (e) => {
    const { name, value } = e.target;
    setEditingFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditingFormData({});
  };

  const handleUpdate = async () => {
    if (!editingFormData.floor || !editingFormData.wardName || !editingFormData.bedNumber) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/wardBed/addWardBed`; // Same API for update
      const token = getAuthToken();

      const payload = {
        ...editingFormData,
        bedNumber: parseInt(editingFormData.bedNumber, 10) || 0,
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to update record");

      toast.success("Record updated successfully!");
      setEditingRowId(null);
      setEditingFormData({});
      fetchWards();
    } catch (error) {
      console.error(error);
      toast.error("Update failed: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const tenantOrClientId = authCtx.tenantOrClientId;
      const apiUrl = `${apiChar}/ipd/wardBed/deleteWardBed?id=${deleteId}&tenantOrClientId=${tenantOrClientId}`;
      const token = getAuthToken();

      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Deletion failed");

      toast.success("Deleted successfully!");
      setShowDeleteConfirm(false);
      setDeleteId(null);
      fetchWards();
    } catch (error) {
      console.error(error);
      toast.error("Delete failed: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <h2 className={classes.pageTitle}>{t("IPD Hospital Configuration")}</h2>

      {/* 🔹 Tabs Section */}


      <div className="row">

        {/* Left Column: Form */}
        <div className="col-md-5">
          <div className={classes.panel}>
            <h4 className={classes.panelTitle}>
              <FaBed className="me-2" /> {formData.id > 0 ? t("Update Details") : t("Add Details")}
            </h4>

            <div className={classes.horizontalField}>
              <Form.Label>{t("Floor Name")}</Form.Label>
              <Form.Control
                type="text"
                name="floor"
                list="floorOptions"
                placeholder={t("Select Floor")}
                value={formData.floor}
                className={classes.formcontrol}
                onChange={handleInputChange}
              />
              <datalist id="floorOptions">
                <option value="First" />
                <option value="Second" />
                <option value="Third" />
                <option value="Fourth" />
                <option value="Fifth" />
              </datalist>
            </div>

            <div className={classes.horizontalField}>
              <Form.Label>{t("Ward / Room")}</Form.Label>
              <Form.Control
                type="text"
                name="wardName"
                list="wardOptions"
                placeholder={t("Select Ward")}
                value={formData.wardName}
                className={classes.formcontrol}
                onChange={handleInputChange}
              />
              <datalist id="wardOptions">
                <option value="General" />
                <option value="Private" />
                <option value="Semi-Private" />
                <option value="ICU" />
                <option value="CCU" />
                <option value="Pediatric" />
                <option value="Maternity" />
              </datalist>
            </div>

            <div className={classes.horizontalField}>
              <Form.Label>{t("Bed Number")}</Form.Label>
              <Form.Control
                type="number"
                name="bedNumber"
                placeholder={t("Enter No")}
                value={formData.bedNumber}
                className={classes.formcontrol}
                onChange={handleInputChange}
              />
            </div>

            <div className={classes.horizontalField}>
              <Form.Label>{t("Description")}</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                rows={2}
                placeholder={t("Description")}
                value={formData.description}
                className={classes.formcontrol}
                onChange={handleInputChange}
              />
            </div>

            <div className="d-flex gap-2">
              <Button
                className="btn-premium"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : (formData.id > 0 ? t("Update") : t("Save"))}
              </Button>
              <Button
                className="btn-secondary-premium"
                onClick={handleClear}
                disabled={isLoading}
              >
                {t("Clear")}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column: Table */}
        <div className="col-md-7">
          <div className={classes.panel}>
            <h4 className={classes.panelTitle}>
              <FaList className="me-2" /> {t("Configured Wards & Beds")}
            </h4>

            <div className="table-responsive mt-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>{t("ID")}</th>
                    <th>{t("Floor")}</th>
                    <th>{t("Ward Name")}</th>
                    <th>{t("Bed Number")}</th>
                    <th>{t("Description")}</th>
                    <th className="text-center">{t("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {wards.length > 0 ? (
                    wards.map((ward) => (
                      <tr key={ward.id}>
                        <td>{ward.id}</td>
                        <td>
                          {editingRowId === ward.id ? (
                            <input
                              type="text"
                              name="floor"
                              value={editingFormData.floor}
                              onChange={handleInlineInputChange}
                              className={`form-control ${classes.tableInput}`}
                            />
                          ) : ward.floor}
                        </td>
                        <td>
                          {editingRowId === ward.id ? (
                            <input
                              type="text"
                              name="wardName"
                              value={editingFormData.wardName}
                              onChange={handleInlineInputChange}
                              className={`form-control ${classes.tableInput}`}
                            />
                          ) : ward.wardName}
                        </td>
                        <td>
                          {editingRowId === ward.id ? (
                            <input
                              type="number"
                              name="bedNumber"
                              value={editingFormData.bedNumber}
                              onChange={handleInlineInputChange}
                              className={`form-control ${classes.tableInput}`}
                            />
                          ) : ward.bedNumber}
                        </td>
                        <td>
                          {editingRowId === ward.id ? (
                            <textarea
                              name="description"
                              value={editingFormData.description}
                              onChange={handleInlineInputChange}
                              rows={1}
                              className={`form-control ${classes.tableInput}`}
                            />
                          ) : (ward.description || "-")}
                        </td>
                        <td className="text-center">
                          {editingRowId === ward.id ? (
                            <div className={classes.inlineActions}>
                              <Button
                                variant="success"
                                size="sm"
                                onClick={handleUpdate}
                                title="Save Changes"
                              >
                                <FaSave />
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                                title="Cancel"
                              >
                                <FaTimes />
                              </Button>
                            </div>
                          ) : (
                            <div className={classes.inlineActions}>
                              <Button
                                className="btn-outline-theme"
                                size="sm"
                                onClick={() => handleEdit(ward)}
                                title="Edit Row"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                className="btn-outline-danger-premium"
                                size="sm"
                                onClick={() => handleDeleteClick(ward.id)}
                                title="Delete Row"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">{t("No configurations found.")}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuration;
